import React, { CSSProperties, ReactNode, useState } from 'react';
import { useCreateHealthCheckMutation } from '../../../generated/graphql';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { FormField, FormTextArea } from '../../form';
import styles from './DuplicateHealthCheckDialog.module.css';

type DuplicateHealthCheckDialogProps = {
    onClose: () => void,
    visible: boolean,
    sourceId: string,
    name: string,
    group: string,
    display: string,
    limit: number,
    description: string,
    settings: string,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const buildDuplicateSettings = (originalSettings: string, sourceId: string): string => {
    try {
        const parsed = originalSettings ? JSON.parse(originalSettings) : {};
        return JSON.stringify({ ...parsed, sourceId });
    } catch {
        return JSON.stringify({ sourceId });
    }
};

const DuplicateHealthCheckDialog = (props: DuplicateHealthCheckDialogProps) => {
    const { sourceId, style, visible, onClose } = props;
    const [createHealthCheck, createHealthCheckResult] = useCreateHealthCheckMutation();
    const [name, setName] = useState(`${props.name} (Copy)`);
    const [group, setGroup] = useState(props.group || '');
    const [display, setDisplay] = useState(props.display || '');
    const [limit, setLimit] = useState(String(props.limit ?? ''));
    const [description, setDescription] = useState(props.description || '');
    const [validationError, setValidationError] = useState('');

    const handleSubmit = () => {
        if (!name || !group || !display || !limit || !description) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        setValidationError('');
        const settings = buildDuplicateSettings(props.settings, sourceId);
        createHealthCheck({
            variables: {
                healthCheck: {
                    name,
                    group,
                    display,
                    limit: Number.parseInt(limit),
                    description,
                    settings,
                }
            }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Duplicate Health Check"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            isLoading={createHealthCheckResult.loading}
            className={styles.DuplicateHealthCheckDialog}
            style={style}
            data-testid="DuplicateHealthCheckDialog">
            <FormField label="Name" name="name" value={name} onChange={setName} />
            <FormField label="Group" name="group" value={group} onChange={setGroup} />
            <FormField label="Display" name="display" value={display} onChange={setDisplay} />
            <FormField label="Limit" name="limit" type="number" value={limit} onChange={setLimit} />
            <FormTextArea label="Description" name="description" value={description} onChange={setDescription} />
        </DialogBase>
    );
};

export default DuplicateHealthCheckDialog;
